import { DateTime } from 'luxon'

import { instantInZone } from '../../ts-utils/luxon-permanent-dst'
import type { SnEvent } from './types'
import type { ScheduleOrder } from './filterEvents'

/** The shape `groupGamesByDay` returns. Structural, so this stays independent
 *  of the game feature's own types. */
export interface DayBucketLike {
  dayKey: string
  games: unknown[]
  tbdGames: unknown[]
}

export type DayRow =
  | { kind: 'game'; game: unknown }
  | { kind: 'event'; event: SnEvent }

export interface MergedDayBucket {
  dayKey: string
  /** Games and events in one render-ready list. */
  items: DayRow[]
}

/**
 * `YYYY-MM-DD` for an event, in the event's OWN zone.
 *
 * Must match how `groupGamesByDay` keys games (venue-local), or an evening
 * event whose UTC instant falls on the next day is filed a day away from the
 * games it belongs beside. Falls back to the viewer's zone when the event
 * carries none, which is what the detail screens display in too.
 */
const dayKeyOf = (event: SnEvent): string => {
  const startsAt = event?.starts_at
  if (!startsAt) {
    return ''
  }
  //  `instantInZone` so the day boundary is the one the province actually
  //  keeps: on a stale timezone database an event in the first hour of a BC
  //  morning keys to the previous day, while the games beside it — keyed
  //  through the corrected `venueLocalDateTime` — do not.
  const dt = event.timezone
    ? instantInZone(startsAt, event.timezone)
    : DateTime.fromISO(startsAt)
  return dt.isValid ? dt.toFormat('yyyy-MM-dd') : ''
}

const startMillis = (startsAt?: string | null): number => {
  const time = startsAt ? new Date(startsAt).getTime() : NaN
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time
}

/**
 * Folds events into the day buckets `groupGamesByDay` produces.
 *
 * Done here rather than by giving `DayOfGames` a mixed `items` prop: that
 * component is shared by MySchedule and OrgSchedule, neither of which has
 * events, so widening it would spread this feature across three pages. The
 * merge stays inside the schedule list instead.
 *
 * Within a day, TBD games keep their band ahead of everything timed — the
 * bucket separates them deliberately, and an event must not break that.
 * Timed games and events then interleave by start time, with the game first
 * on a tie so its position never shifts as events load in beside it.
 *
 * A day carrying only events gets a bucket of its own, so an event-only date
 * still renders a heading.
 */
export const mergeEventsIntoDayBuckets = (
  buckets: DayBucketLike[],
  events: SnEvent[] | null | undefined,
  order: ScheduleOrder = 'asc'
): MergedDayBucket[] => {
  const byDay = new Map<string, { tbd: DayRow[]; timed: DayRow[] }>()

  const slot = (dayKey: string) => {
    let entry = byDay.get(dayKey)
    if (!entry) {
      entry = { tbd: [], timed: [] }
      byDay.set(dayKey, entry)
    }
    return entry
  }

  buckets.forEach((bucket) => {
    const entry = slot(bucket.dayKey)
    bucket.tbdGames.forEach((game) => entry.tbd.push({ kind: 'game', game }))
    bucket.games.forEach((game) => entry.timed.push({ kind: 'game', game }))
  })
  ;(events ?? []).forEach((event) => {
    const dayKey = dayKeyOf(event)
    if (dayKey === '') {
      return
    }
    slot(dayKey).timed.push({ kind: 'event', event })
  })

  const rowStart = (row: DayRow): number =>
    row.kind === 'event'
      ? startMillis(row.event.starts_at)
      : startMillis((row.game as { starts_at?: string | null })?.starts_at)

  const merged: MergedDayBucket[] = Array.from(byDay.entries()).map(
    ([dayKey, entry]) => {
      entry.timed.sort((a, b) => {
        const diff = rowStart(a) - rowStart(b)
        if (diff !== 0) {
          return diff
        }
        // Stable tie-break: games hold their place, events settle in after.
        if (a.kind === b.kind) {
          return 0
        }
        return a.kind === 'game' ? -1 : 1
      })
      return { dayKey, items: [...entry.tbd, ...entry.timed] }
    }
  )

  merged.sort((a, b) => {
    if (a.dayKey === b.dayKey) {
      return 0
    }
    const ascending = a.dayKey < b.dayKey ? -1 : 1
    return order === 'desc' ? -ascending : ascending
  })

  return merged
}
